import React, { useState } from 'react';
import * as Yup from 'yup';
import { Button, Input } from '../../Components';
import { useApi } from '../../Helpers/ApiHook';
import CustomAlert from '../../Components/CustomAlert';

const contactSchema = Yup.object().shape({
    first_name: Yup.string().required('First name is required'),
    last_name: Yup.string().required('Last name is required'),
    email: Yup.string()
        .email('Enter a valid email')
        .required('Email is required'),
    phone: Yup.string()
        .matches(/^\+?[0-9]{7,15}$/, 'Enter a valid phone number')
        .required('Phone number is required'),
    message: Yup.string()
        .min(10, 'Message should be at least 10 characters')
        .required('Message is required'),
});

const ContactUsForm = ({ isHideLabel = false, inputClass = "" }) => {
    const { post } = useApi()
    const [values, setValues] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        message: '',
    });

    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setValues((prev) => ({ ...prev, [name]: value }));
        // clear error for this field on change
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const validateField = async (name, value) => {
        try {
            await contactSchema.validateAt(name, { ...values, [name]: value });
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        } catch (err) {
            setErrors((prev) => ({ ...prev, [name]: err.message }));
        }
    };

    const handleBlur = (e) => {
        const { name, value } = e.target;
        validateField(name, value);
    };


    const handleSubmit = async (e) => {

        e.preventDefault();
        await setIsSubmitting(true);

        try {

            // full form validation
            await contactSchema.validate(values, { abortEarly: false });
            setErrors({});
            onSubmitDetails()
        } catch (err) {
            if (err.name === 'ValidationError') {
                // collect Yup errors
                const formErrors = {};
                err.inner.forEach((e) => {
                    if (e.path && !formErrors[e.path]) {
                        formErrors[e.path] = e.message;
                    }
                });
                setErrors(formErrors);
            } else {
                console.error(err);
                setIsSubmitting(false);
            }
        }
    }

    const onSubmitDetails = async (e) => {

        try {
            // ---- API CALL (change URL to your real endpoint) ----
            const res = await post('/contact/submit', values);

            if (res.status) {
                CustomAlert({
                    type: "success",
                    title: "Contact Submission Successful!",
                    message: "Thank you! Your message has been sent.",
                    confirmText: 'Okay',
                    onConfirm: () => { return; }
                })
                setValues({
                    first_name: '',
                    last_name: '',
                    email: '',
                    phone: '',
                    message: '',
                });
            } else {
                alert('Failed to submit. Please try again later.');
                CustomAlert({
                    type: "error",
                    title: "Contact Submission Failed!",
                    message: "Failed to submit. Please try again later.",
                    confirmText: 'Okay',
                    onConfirm: () => { return; }
                })
            }

        } catch (err) {
            console.error(err);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form className="space-y-5" onSubmit={handleSubmit} noValidate>
            {/* First / Last name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <Input
                    isHideLabel={isHideLabel}
                    className={inputClass}
                    label="First Name"
                    placeholder="First Name"
                    name="first_name"
                    value={values.first_name}
                    onChange={handleChange}
                    // onBlur={handleBlur}
                    errors={errors}
                />

                <Input
                    isHideLabel={isHideLabel}
                    className={inputClass}
                    label="Last Name"
                    placeholder="Last Name"
                    name="last_name"
                    value={values.last_name}
                    onChange={handleChange}
                    // onBlur={handleBlur}
                    errors={errors}
                />
            </div>

            {/* Email / Phone */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                <Input
                    isHideLabel={isHideLabel}
                    className={inputClass}
                    type='email'
                    label="Email"
                    placeholder="Email"
                    name="email"
                    value={values.email}
                    onChange={handleChange}
                    // onBlur={handleBlur}
                    errors={errors}
                />
                <Input
                    isHideLabel={isHideLabel}
                    className={inputClass}
                    type='tel'
                    label="Phone Number"
                    placeholder="Phone Number"
                    name="phone"
                    value={values.phone}
                    onChange={handleChange}
                    // onBlur={handleBlur}
                    errors={errors}
                />
            </div>

            {/* Message */}
            <Input
                isHideLabel={isHideLabel}
                className={inputClass}
                type='textarea'
                label="What Do You Have in Mind"
                placeholder="Please enter query..."
                name="message"
                value={values.message}
                onChange={handleChange}
                // onBlur={handleBlur}
                errors={errors}
                rows={5}
            />

            {/* Submit button + status */}
            <div className="flex flex-col items-center pt-2">
                <Button
                    type="submit"
                    width="w-full"
                    className="rounded-md"
                    disabled={isSubmitting}
                >
                    {isSubmitting ? 'Submitting…' : 'Submit'}
                </Button>

            </div>
        </form>
    );
};

export { ContactUsForm };
